import * as Sentry from '@sentry/react';
import { call, put, fork, select, takeLatest } from 'redux-saga/effects';
import { navigate } from 'navigation';
import { setAuthToken, setUser, noop, sessionLoadFailed } from './actions';
import { SIGN_OUT, FETCH_SESSION } from './constants';
import { signOut } from './actions';
import { getItem, removeItem } from 'utils/localStorage';
import { getSession, deleteSession } from 'utils/api';
import { selectAuthToken } from './selectors';
import { authedApiSaga } from 'sagas';
import { clearState } from 'reducers';
import { identify as posthogIdentify, reset as posthogReset } from 'utils/posthog';

// Classify a getSession rejection into a FIXED enum (never the raw error) so a Sentry
// report can still tell "the network dropped" from "the request timed out" from "the
// server returned a 5xx" apart, without forwarding any backend-controlled content.
// request.js's three transient shapes: `{ statusCode }` from a non-2xx response,
// `{ error: 'Request timed out...' }` from its own AbortController timeout, or a raw
// fetch-level Error/TypeError (e.g. "Failed to fetch") for everything else — a dropped
// connection, DNS failure, CORS, etc.
export function categorizeTransientFailure(error) {
  if (typeof error?.statusCode === 'number') return 'http-error';
  if (error?.error === 'Request timed out. Please check your connection and try again.') return 'timeout';
  return 'network';
}

export function* getSessionFlow() {
  let response;

  // The guarded region is exactly "obtain the session", and no more. It used to
  // run to the end of the flow, which was harmless while the catch did nothing
  // on a transient error — but once that arm records a FAILURE, a throw from a
  // step AFTER a successful fetch would be filed as one, with `setUser` already
  // dispatched. That hands a route waiting on the session an error state over a
  // perfectly good session. Narrowing the try is what makes the recorded
  // failure mean what it says.
  try {
    const token = yield select(selectAuthToken());
    response = yield call(getSession, token);
  } catch (error) {
    // Only sign out on auth errors; the 401 interceptor in request.js handles
    // expired tokens globally.
    if (error?.statusCode === 401 || error?.statusCode === 403 || error?.error === 'Session expired. Please sign in again.') {
      yield put(signOut());
    } else {
      // A transient failure — network drop, timeout, 5xx. This branch used to
      // swallow it entirely, which was survivable only for as long as nothing
      // WAITED on the session: the token is already in the store and App
      // routes on the token alone, so the app stays authenticated with a user
      // that never populates and nothing left to retry it. Any route that
      // waits for the session would wait forever. Recording it gives those
      // routes a terminal state to render, and a retry to offer.
      //
      // It was also invisible to us — no console/Sentry trace, so operators
      // could not tell a login-bootstrap problem from a quiet one. Report it
      // the same way ErrorBoundary reports a render crash Sentry's own
      // auto-instrumentation would otherwise miss.
      //
      // Deliberately NOT forwarding `error` itself: request.js's parseJSON
      // rejects with `{ ...body, statusCode }`, spreading the backend's raw
      // JSON error body — which can echo arbitrary fields (validation errors
      // often include the submitted value) — onto the rejection. Sending that
      // unchanged would make this report only as private as the least private
      // field any endpoint ever put in an error body. Forward a fixed message
      // plus a closed-enum `category` (network / timeout / http-error, see
      // categorizeTransientFailure) and the status code when there is one —
      // enough to tell the three transient shapes apart without forwarding
      // any backend-controlled content.
      Sentry.captureException(new Error('Transient session-restore failure'), {
        contexts: {
          saga: { name: 'getSessionFlow', phase: 'session-restore' },
          response: {
            category: categorizeTransientFailure(error),
            statusCode: error?.statusCode ?? null,
          },
        },
      });
      yield put(sessionLoadFailed());
    }
    return;
  }

  yield put(setUser(response));

  // Identify the PostHog distinct_id with the authenticated user so flag
  // rollouts can target by user id / email. Helper is a no-op if PostHog
  // wasn't initialized (e.g. token env var missing in local dev).
  //
  // Guarded separately, and deliberately outside the region above: the session
  // is already in the store by now, so an analytics SDK throwing here is not a
  // session failure and must not be recorded as one. Same defensive treatment
  // posthogReset gets in deleteSessionFlow.
  try {
    yield call(posthogIdentify, response);
  } catch (_e) {
    // An analytics identity is not worth degrading a working session over.
  }
}

export function* deleteSessionFlow() {
  yield call(authedApiSaga, deleteSession, [], noop, noop);
  yield call(removeItem, 'authToken');
  // Drop the PostHog distinct_id binding so the next anonymous visitor isn't
  // attributed to the previous user. Must run before clearState so any
  // posthog.reset() side-effects don't race the auth wipe. Guarded so that
  // a hypothetical SDK throw at this point can't strand the redux store in
  // the authenticated state after the auth token has already been removed.
  try {
    yield call(posthogReset);
  } catch (_e) {
    // posthog.reset() is normally infallible; this is a belt-and-suspenders
    // guard so sign-out completes even if a future SDK regression throws.
  }
  yield put(clearState());
  yield call(navigate, '/');
}

export default function* appSaga() {
  const token = yield call(getItem, 'authToken');

  // Watch before the initial fetch, so a retry dispatched by a route is never
  // dropped for arriving before the watcher exists.
  yield takeLatest(FETCH_SESSION, getSessionFlow);

  if (token) {
    yield put(setAuthToken(token));
    yield fork(getSessionFlow);
  }

  yield takeLatest(SIGN_OUT, deleteSessionFlow);
}
